import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdsAssistantOrgLocations } from "@/components/ads-assistant/useAdsAssistantOrgLocations";
import { AdsAssistantFeedImportStatusBanner } from "@/components/ads-assistant/AdsAssistantFeedImportStatusBanner";
import { useAdsAssistantFeedImport } from "@/components/ads-assistant/useAdsAssistantFeedImport";

type Props = {
  scopedOrgId: string | null;
  onAfterSync?: () => void;
};

/** Full-width CRM Social Planner sync body (Settings location). */
export function AdsAssistantIngestCrmSyncContent({ scopedOrgId, onAfterSync }: Props) {
  const auth = useAuth();
  const { locations, locationsLoaded } = useAdsAssistantOrgLocations(scopedOrgId);

  const settingsLocationId = useMemo(() => {
    if (!locationsLoaded || !locations.length) return null;
    const orgIds = new Set(locations.map((l) => l.id));
    const preferred = auth.activeLocationId?.trim() || "";
    if (preferred && orgIds.has(preferred)) return preferred;
    const allowed = (auth.accessibleLocationIds ?? []).map((id) => id.trim()).filter(Boolean);
    const match = allowed.find((id) => orgIds.has(id));
    if (match) return match;
    if (locations.length === 1) return locations[0]!.id;
    return null;
  }, [locations, locationsLoaded, auth.activeLocationId, auth.accessibleLocationIds]);

  const locationName = useMemo(() => {
    if (!settingsLocationId) return null;
    return locations.find((l) => l.id === settingsLocationId)?.name?.trim() || null;
  }, [locations, settingsLocationId]);

  const vm = useAdsAssistantFeedImport(scopedOrgId, { salesGhlOnly: true, settingsLocationId });

  async function runSync(fn: () => Promise<void>) {
    await fn();
    onAfterSync?.();
  }

  return (
    <div className="w-full space-y-3 border-t border-l-2 border-l-indigo-300 border-gray-200 px-3 py-3 dark:border-gray-600 dark:border-l-indigo-600">
      <p className="text-[11px] leading-snug text-gray-600 dark:text-gray-400">
        Pulls published Facebook and Google posts scheduled in your CRM. Uses the location selected in your account{" "}
        <strong className="font-medium text-gray-800 dark:text-gray-200">Settings</strong>.
      </p>

      <AdsAssistantFeedImportStatusBanner scopedOrgId={scopedOrgId} />

      <div className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-600 dark:bg-zinc-900/80">
        <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Active location
        </div>
        {!locationsLoaded ? (
          <p className="mt-0.5 text-sm text-gray-500">Loading…</p>
        ) : locationName ? (
          <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-gray-100">{locationName}</p>
        ) : (
          <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-200">
            None set — choose a location in Settings to sync.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
        <label className="text-[11px] text-gray-600 dark:text-gray-400">
          Days back (1–90)
          <input
            type="number"
            min={1}
            max={90}
            disabled={!settingsLocationId || vm.busy}
            className="mt-0.5 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-zinc-900"
            value={vm.daysBack}
            onChange={(e) => vm.setDaysBack(Math.min(90, Math.max(1, Number(e.target.value) || 30)))}
          />
        </label>
        <label className="text-[11px] text-gray-600 dark:text-gray-400">
          Max posts (1–100)
          <input
            type="number"
            min={1}
            max={100}
            disabled={!settingsLocationId || vm.busy}
            className="mt-0.5 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-zinc-900"
            value={vm.fetchLimit}
            onChange={(e) => vm.setFetchLimit(Math.min(100, Math.max(1, Number(e.target.value) || 25)))}
          />
        </label>
      </div>

      <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          disabled={vm.busy || !vm.importReady}
          onClick={() => void runSync(async () => vm.runImport())}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500"
        >
          {vm.busy ? "Working…" : "Sync from CRM"}
        </button>
        <button
          type="button"
          disabled={vm.busy || !settingsLocationId}
          onClick={() => void runSync(async () => vm.runReconcileLocationTags())}
          className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-100"
        >
          Fix location tags
        </button>
      </div>

      {vm.error ? <p className="text-[11px] text-red-600 dark:text-red-400">{vm.error}</p> : null}
      {vm.result ? <p className="text-[11px] text-emerald-800 dark:text-emerald-300">{vm.result}</p> : null}
    </div>
  );
}
